class SSEService {
  constructor() {
    this.eventSource = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.customerId = null;
  }

  connect(customerId) {
    if (this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) {
      console.log('SSE connection already exists');
      return;
    }

    if (!customerId) {
      console.error('Customer ID is required for SSE connection');
      return;
    }

    // Store customerId for reconnection attempts
    this.customerId = customerId;

    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const url = `${baseUrl}/api/sse/template-updates/${customerId}`;
    
    console.log('Connecting to SSE for customer:', customerId, 'URL:', url);
    
    this.eventSource = new EventSource(url);
    
    this.eventSource.onopen = () => {
      console.log('SSE connection opened');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
    };

    this.eventSource.onmessage = (event) => {
      console.log('SSE message received:', event);
      try {
        const data = JSON.parse(event.data);
        this.notifyListeners('message', data);
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    this.eventSource.addEventListener('connected', (event) => {
      console.log('SSE connected:', event.data);
      this.notifyListeners('connected', JSON.parse(event.data));
    });

    this.eventSource.addEventListener('template-update', (event) => {
      console.log('Template update received:', event.data);
      try {
        const data = JSON.parse(event.data);
        this.notifyListeners('template-update', data);
      } catch (error) {
        console.error('Error parsing template update:', error);
      }
    });

    this.eventSource.addEventListener('template-created', (event) => {
      console.log('Template created received:', event.data);
      try {
        const data = JSON.parse(event.data);
        this.notifyListeners('template-created', data);
      } catch (error) {
        console.error('Error parsing template created:', error);
      }
    });

    this.eventSource.addEventListener('template-deleted', (event) => {
      console.log('Template deleted received:', event.data);
      try {
        const data = JSON.parse(event.data);
        this.notifyListeners('template-deleted', data);
      } catch (error) {
        console.error('Error parsing template deleted:', error);
      }
    });

    this.eventSource.addEventListener('CHAT_MESSAGE', (event) => {
      try {
        const data = JSON.parse(event.data);
        this.notifyListeners('CHAT_MESSAGE', data);
      } catch (error) {
        console.error('Error parsing CHAT_MESSAGE event:', error);
      }
    });

    this.eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      this.handleReconnect();
    };

    this.eventSource.onclose = () => {
      console.log('SSE connection closed');
      this.handleReconnect();
    };
  }

  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.notifyListeners('connection-failed', { error: 'Max reconnection attempts reached' });
      return;
    }

    if (!this.customerId) {
      console.error('Cannot reconnect: customerId not available');
      this.notifyListeners('connection-failed', { error: 'Customer ID not available for reconnection' });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connect(this.customerId);
    }, delay);
  }

  disconnect() {
    if (this.eventSource) {
      console.log('Disconnecting SSE');
      this.eventSource.close();
      this.eventSource = null;
    }
    this.customerId = null;
    this.reconnectAttempts = 0;
  }

  addEventListener(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
  }

  removeEventListener(eventType, callback) {
    if (this.listeners.has(eventType)) {
      const callbacks = this.listeners.get(eventType);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  notifyListeners(eventType, data) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in SSE listener callback:', error);
        }
      });
    }
  }

  getConnectionStatus() {
    if (!this.eventSource) {
      return 'disconnected';
    }
    
    switch (this.eventSource.readyState) {
      case EventSource.CONNECTING:
        return 'connecting';
      case EventSource.OPEN:
        return 'connected';
      case EventSource.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }
}

// Create a singleton instance
const sseService = new SSEService();

export default sseService;
